import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

import {
  TABLE_ANALYTICAL_NOTES_LIST,
  PAGE_SIZE,
  ANALYTICAL_NOTES_LIST_ENDPOINT,
} from "@utils/constants";
import { loadAllAnalyticalNotes } from "./AnalyticalNotesList.actionCreators";
import { setAllAnalyticalNotesLoadingState } from "./AnalyticalNotesList.slice";
import EncloseTable from "@shared/baseUI/EnhancedTable/EncloseTable";
import AnalyticalId from "./components/AnalyticalId/AnalyticalId";
import AnalyticalStatus from "./components/AnalyticalStatus/AnalyticalStatus";
import AnalyticalAction from "./components/AnalyticalAction/AnalyticalAction";
import { columnHeader } from "@utils/utils";
import { getDateFormat } from "@utils/datePickerUtils";

const PrepareDataTable = (props) => {
  const {
    t,
    isRefreshList,
    updateRefeshStatus,
    columnList,
    createNewAction,
    siteId,
  } = props;

  const [data, setData] = useState([]);
  const [isRefreshDataAgain, setRefreshState] = useState(false);
  const [skipPageReset, setSkipPageReset] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState({});

  const dataURL = ANALYTICAL_NOTES_LIST_ENDPOINT;
  const dispatch = useDispatch();
  const refreshData = (data) => {
    setRefreshState(data);
  };

  let columns = [
    {
      Header: t("ID"),
      title: "ID",
      accessor: "id",
      Cell: ({ cell: { value } }) => (
        <AnalyticalId
          id={value}
          siteId={siteId}
          updateRefeshStatus={updateRefeshStatus}
        />
      ),
      disableFilters: true,
    },
    {
      Header: t("STATUS"),
      title: "STATUS",
      accessor: "status",
      Cell: ({ cell: { value } }) => <AnalyticalStatus status={value} />,
      disableFilters: true,
    },
    {
      Header: t("CREATED_BY"),
      title: "CREATED_BY",
      accessor: "createdBy",
      disableFilters: true,
    },
    {
      Header: t("CREATION_DATE"),
      title: "CREATION_DATE",
      accessor: "createdAt",
      disableFilters: true,
      Cell: ({ cell: { value } }) =>
        value ? getDateFormat(value, "YYYY-MM-DD") : "",
    },
    {
      Header: t("UPDATED_AT"),
      title: "UPDATED_AT",
      accessor: "updatedAt",
      disableFilters: true,
      Cell: ({ cell: { value } }) =>
        value ? getDateFormat(value, "YYYY-MM-DD") : "",
    },
    {
      Header: t("ACTIONS"),
      title: "ACTIONS",
      accessor: "action",
      Cell: ({ cell: { value, column } }) => (
        <AnalyticalAction
          data={value}
          column={column}
          onRefreshData={refreshData}
          updateRefeshStatus={updateRefeshStatus}
        />
      ),
      disableFilters: true,
    },
  ];

  columns = columnHeader(columns, columnList);

  const analyticalNotesListByIds = useSelector(
    (state) => state.analyticalNotesList.byId
  );
  const analyticalNotesListAllIds = useSelector(
    (state) => state.analyticalNotesList.allIds
  );
  const filteredRecords = useSelector(
    (state) => state.analyticalNotesList.filteredRecords
  );

  const isAnalyticalNotesListLoading = useSelector(
    (state) => state.analyticalNotesList.isLoading
  );

  useEffect(() => {
    setLoading(isAnalyticalNotesListLoading);
  }, [isAnalyticalNotesListLoading]);

  /**
   * Method to fetch set of records based on the filters applied.
   * @param {pageSize} pageSize
   * @param {pageStart} pageStart
   * @param {globalFilter} globalFilter
   * @param {sort} sort
   * @param {appliedFilters} appliedFilters
   */
  const fetchRecords = ({
    pageSize,
    pageStart,
    globalFilter,
    sort,
    appliedFilters,
    currentPage,
  }) => {
    // save request data,
    setRequestData({ pageSize, pageStart, globalFilter, sort, appliedFilters });
    // Set the loading state
    setLoading(true);

    dispatch(setAllAnalyticalNotesLoadingState());
    dispatch(
      loadAllAnalyticalNotes({
        dataURL,
        siteId: siteId ?? "",
        pageSize,
        currentPage,
        sort,
        search: globalFilter,
        status: 1,
      })
    );
  };

  useEffect(() => {
    if (analyticalNotesListAllIds.length > 0) {
      const responseData = analyticalNotesListAllIds.map((id) => {
        const tempData = { ...analyticalNotesListByIds[id] };
        tempData.createdBy =
          tempData?.author?.firstName + " " + tempData?.author?.lastName;
        tempData.createdAt = tempData.createdAt || "";
        tempData.updatedAt = tempData.updatedAt || ""
        tempData.action = {
          id: tempData.id,
          canEdit: tempData.canEdit,
          siteId: tempData.siteId,
        };
        return tempData;
      });
      setData([...responseData]);
      setPageCount(filteredRecords);
      setLoading(false);
    } else {
      setData([]);
      setPageCount(0);
      setLoading(false);
    }
  }, [t, analyticalNotesListAllIds, analyticalNotesListByIds, filteredRecords]);

  useEffect(() => {
    if (isRefreshDataAgain) {
      setRefreshState(false);
    }
    if (isRefreshList) {
      setRefreshState(true);
    }
  }, [isRefreshDataAgain, isRefreshList]);

  return (
    <EncloseTable
      type={TABLE_ANALYTICAL_NOTES_LIST}
      columns={columns}
      data={data}
      setData={setData}
      initialPageSize={PAGE_SIZE}
      isRefreshDataAgain={isRefreshDataAgain}
      setSkipPageReset={setSkipPageReset}
      skipPageReset={skipPageReset}
      pageCount={pageCount}
      setPageCount={setPageCount}
      fetchRecords={fetchRecords}
      loading={loading}
      defaultSortOptions={[{ id: "id", desc: true, dir: "desc" }]}
      createNewAction={createNewAction}
    />
  );
};

PrepareDataTable.propTypes = {
  t: PropTypes.func.isRequired,
  isRefreshList: PropTypes.bool.isRequired,
  columnList: PropTypes.array.isRequired,
};

export default withTranslation()(PrepareDataTable);
